/**
 * Marketing pixel events — TikTok, Taboola, GTM dataLayer.
 *
 * Fans every funnel event out to all three pixel networks. Each
 * dispatch is wrapped in safe-guards so an ad-blocker, stealth mode,
 * SSR snapshot, or unloaded SDK never breaks the user experience.
 *
 * Why a single helper instead of per-pixel calls scattered through
 * the codebase:
 *   - One file to audit when we add / remove pixels.
 *   - Consistent event naming across pixels (TikTok uses PascalCase,
 *     Taboola uses snake_case; we normalize once here).
 *   - Stealth-mode kill switch in one place — same contract as
 *     `lib/analytics.js`.
 *
 * TikTok's vertical-funnel events (per their pixel diagnostics):
 *   ViewContent    — User viewed pricing or a personalized routine.
 *   AddToCart      — User clicked the "Subscribe" / "Upgrade" CTA.
 *   InitiateCheckout — User was redirected to Stripe Checkout.
 *   CompletePayment — User landed on the post-payment success page.
 *
 * All four are essential for TikTok's campaign optimization (it
 * downgrades pixels missing them per the dashboard warning).
 */

import { safeLocal } from "./safeStorage";
import { STEALTH_KEY } from "./storageKeys";

// Taboola account id — must match the snippet in public/index.html.
const TABOOLA_ACCOUNT_ID = 2047230;

const isBrowser = () => typeof window !== "undefined";
const isStealth = () =>
  isBrowser() ? safeLocal.getItem(STEALTH_KEY) === "1" : false;

// ─── Per-pixel safe dispatchers ────────────────────────────────────
// Each `safe*` function is a no-op if the SDK isn't loaded
// (ad-blocker, slow network) — we never throw. We catch every
// SDK call because some pixel SDKs throw synchronously on malformed
// payloads, and marketing telemetry should NEVER break the UX.

const safeTiktok = (eventName, props) => {
  if (!isBrowser() || !window.ttq || typeof window.ttq.track !== "function") {
    return;
  }
  try {
    window.ttq.track(eventName, props || {});
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[pixels] ttq.track threw:", err?.message || err);
    }
  }
};

const safeTaboola = (eventName, extra) => {
  if (!isBrowser() || !Array.isArray(window._tfa)) return;
  try {
    window._tfa.push({
      notify: "event",
      name: eventName,
      id: TABOOLA_ACCOUNT_ID,
      ...(extra || {}),
    });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[pixels] taboola push threw:", err?.message || err);
    }
  }
};

const safeDataLayer = (eventName, props) => {
  if (!isBrowser() || !Array.isArray(window.dataLayer)) return;
  try {
    window.dataLayer.push({ event: eventName, ...(props || {}) });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[pixels] dataLayer push threw:", err?.message || err);
    }
  }
};

// ─── Public API — fan-out funnel events ────────────────────────────

/**
 * User viewed content of interest — pricing page or personalized
 * routine. Light-weight event TikTok uses for "browser" audiences.
 */
export const trackViewContent = (props = {}) => {
  if (isStealth()) return;
  safeTiktok("ViewContent", props);
  safeTaboola("view_content");
  safeDataLayer("view_content", props);
};

/**
 * User signaled purchase intent — clicked Subscribe / Upgrade CTA
 * but before the Stripe redirect. Treats the subscription as the
 * cart item being added.
 */
export const trackAddToCart = (props = {}) => {
  if (isStealth()) return;
  safeTiktok("AddToCart", props);
  safeTaboola("add_to_cart");
  safeDataLayer("add_to_cart", props);
};

/**
 * User was redirected to Stripe Checkout. Strong intent — TikTok
 * uses this to build conversion-optimized audiences.
 */
export const trackInitiateCheckout = (props = {}) => {
  if (isStealth()) return;
  safeTiktok("InitiateCheckout", props);
  safeTaboola("start_checkout");
  safeDataLayer("initiate_checkout", props);
};

/**
 * User completed the Stripe payment — fired on /billing/success,
 * /lock-in/success, /pride/success. ALWAYS pass `value` (number,
 * USD dollars) and `currency` (3-letter code) so TikTok can bid
 * for ROAS, not just signups.
 */
export const trackPurchase = ({ value, currency, ...props } = {}) => {
  if (isStealth()) return;
  const payload = {
    value: typeof value === "number" ? value : undefined,
    currency: currency || "USD",
    ...props,
  };
  // TikTok's standard event for SaaS subscriptions is CompletePayment.
  // PlaceAnOrder is for one-shot physical commerce.
  safeTiktok("CompletePayment", payload);
  safeTaboola("purchase", { value: payload.value });
  safeDataLayer("purchase", payload);
};
